import * as Sentry from '@sentry/react';
import { Component, FC, PropsWithChildren, useEffect } from 'react';

import { useAnalytics } from '@hooks/useAnalytics';

import { EmptyState } from './EmptyState/EmptyState';

interface ErrorStateProps {
  hideOnError?: boolean;
  error?: unknown;
  errorInfo?: unknown;
}

const ErrorState: FC<ErrorStateProps> = ({ error, errorInfo, hideOnError }) => {
  const { track } = useAnalytics();

  useEffect(() => {
    track('Caught Error', {
      error,
      errorInfo,
    });
  }, []);

  const handleClick = () => {
    window.location.reload();
  };

  if (hideOnError) {
    return <></>;
  }

  return (
    <div className="p-3">
      <EmptyState
        title="Uh oh!"
        text="Something went wrong displaying this screen"
        button={{ text: 'Reload', onClick: handleClick }}
      />
    </div>
  );
};

interface ErrorBoundaryProps {
  hideOnError?: boolean;
  onError?: (error?: unknown, errorInfo?: unknown) => void;
}

interface ErrorBoundaryState extends ErrorStateProps {
  hasError?: boolean;
}

export class ErrorBoundary extends Component<
  PropsWithChildren<ErrorBoundaryProps>,
  ErrorBoundaryState
> {
  state = { hasError: false, error: null, errorInfo: null };

  constructor(props) {
    super(props);
  }

  static getDerivedStateFromError(error) {
    Sentry.captureException(error);
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    this.props.onError?.(error, errorInfo);
  }

  render() {
    const { hasError, ...rest } = this.state;

    if (hasError) {
      return <ErrorState hideOnError={this.props.hideOnError} {...rest} />;
    }

    return this.props.children;
  }
}
